import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box } from '@material-ui/core';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDialog?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  showDialog: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      showDialog: props.showDialog !== false 
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    if ((window as any).Bugsnag) {
      (window as any).Bugsnag.notify(error, (event: any) => {
        event.addMetadata('errorInfo', {
          componentStack: errorInfo.componentStack,
          errorBoundary: true
        });
      });
    }
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: undefined, 
      errorInfo: undefined 
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleCloseDialog = () => {
    this.setState({ showDialog: false });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const errorMessage = this.state.error?.message || 'An unexpected error occurred';
      const isDevelopment = process.env.NODE_ENV === 'development';

      if (this.state.showDialog) {
        return (
          <Dialog 
            open={true} 
            maxWidth="md" 
            fullWidth
            onClose={this.handleCloseDialog}
          >
            <DialogTitle>
              <Typography variant="h6" color="error">
                Something went wrong
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box 
                style={{ 
                  backgroundColor: '#ffebee', 
                  color: '#c62828', 
                  padding: 16, 
                  borderRadius: 4, 
                  marginBottom: 16,
                  border: '1px solid #ffcdd2'
                }}
              >
                {errorMessage}
              </Box>
              
              <Typography variant="body2" color="textSecondary" paragraph>
                An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.
              </Typography>

              {isDevelopment && this.state.error && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Error Details (Development):
                  </Typography>
                  <Box 
                    component="pre" 
                    style={{ 
                      backgroundColor: '#f5f5f5', 
                      padding: 12, 
                      borderRadius: 4,
                      fontSize: '12px',
                      overflow: 'auto',
                      maxHeight: 200
                    }}
                  >
                    {this.state.error.stack}
                  </Box>
                </Box>
              )}

              {isDevelopment && this.state.errorInfo && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Component Stack (Development):
                  </Typography>
                  <Box 
                    component="pre" 
                    style={{ 
                      backgroundColor: '#f5f5f5', 
                      padding: 12, 
                      borderRadius: 4,
                      fontSize: '12px',
                      overflow: 'auto',
                      maxHeight: 200
                    }}
                  >
                    {this.state.errorInfo.componentStack}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={this.handleRetry} color="primary">
                Try Again
              </Button>
              <Button onClick={this.handleReload} color="primary" variant="contained">
                Refresh Page
              </Button>
            </DialogActions>
          </Dialog>
        );
      }

      return (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          minHeight="200px"
          p={3}
        >
          <Box 
            style={{ 
              backgroundColor: '#ffebee', 
              color: '#c62828', 
              padding: 16, 
              borderRadius: 4, 
              marginBottom: 16,
              maxWidth: 600,
              border: '1px solid #ffcdd2'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body2">
              {errorMessage}
            </Typography>
          </Box>
          
          <Box display="flex" style={{ gap: 16 }}>
            <Button onClick={this.handleRetry} color="primary">
              Try Again
            </Button>
            <Button onClick={this.handleReload} color="primary" variant="contained">
              Refresh Page
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
